import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  AUTOSCALING_STATE_PARAM_KEY,
} from '~/shared/utils/filterUtils';
import { SelectOption } from '~/shared/utils/types';

import { apiFilterKey } from '../../../../common/serachParamKeys';

const TAG_FIELD_RE = /^tag:(.+)$/; // captures tag key after 'tag:'
const VALUE_SPLIT_RE = /\s*,\s*/; // split comma-separated values

export interface AppliedFiltersResult {
  appliedClusterNameFilters: SelectOption[];
  appliedInstanceFilters: SelectOption[];
  appliedVolumeIdFilters: SelectOption[];
  appliedVolumeTagFilters: SelectOption[];
  appliedAccountFilters: SelectOption[];
  appliedAutoscaleState: string[];
  activeFilterCount: number;
  currentBaseFilters: SelectOption[];
}

export function useAppliedFilters(): AppliedFiltersResult {
  const [searchParams] = useSearchParams();
  const apiFilters = searchParams.get(apiFilterKey);
  const currentBaseFilters = useMemo(
    () => JSON.parse(apiFilters || '[]') as SelectOption[],
    [apiFilters]
  );

  return useMemo(() => {
    const processedFilters = searchParams.get('processed_filters');
    const appliedClusterNameFilters: SelectOption[] = [];
    const appliedInstanceFilters: SelectOption[] = [];
    const appliedVolumeIdFilters: SelectOption[] = [];
    const appliedVolumeTagFilters: SelectOption[] = [];
    const appliedAccountFilters: SelectOption[] = [];

    if (processedFilters) {
      try {
        const parsedProcessedFilters = JSON.parse(processedFilters) as string[];
        parsedProcessedFilters.forEach((filterStr) => {
          const filter = JSON.parse(filterStr);
          const { field, value, op } = filter;
          if ((op === 'in' || op === 'equals') && typeof value === 'string') {
            const values = value.split(VALUE_SPLIT_RE);
            values.forEach((val: string) => {
              const cleanVal = val.trim();
              const tagFieldMatch = typeof field === 'string' ? TAG_FIELD_RE.exec(field) : null;
              const isTag = Boolean(tagFieldMatch);
              const tagKey = tagFieldMatch ? tagFieldMatch[1] : '';
              const compositeId = isTag ? `${tagKey}:${cleanVal}` : cleanVal;
              const option: SelectOption = {
                id: compositeId,
                name: isTag ? `${tagKey}:${cleanVal}` : `${cleanVal}`,
                group: field as SelectOption['group'],
              };
              if (field === 'clusterName') {
                appliedClusterNameFilters.push(option);
              }
              if (field === 'instanceId') {
                appliedInstanceFilters.push(option);
              }
              if (field === 'volumeId') {
                appliedVolumeIdFilters.push(option);
              }
              if (field === 'accountId') {
                appliedAccountFilters.push(option);
              }
              if (isTag) {
                appliedVolumeTagFilters.push(option);
              }
            });
          }
        });
      } catch {
        // swallow parse errors silently – fallback below
      }
    } else if (apiFilters) {
      appliedClusterNameFilters.push(
        ...currentBaseFilters.filter((f) => f.group === 'clusterName')
      );
      appliedInstanceFilters.push(...currentBaseFilters.filter((f) => f.group === 'instanceId'));
      appliedVolumeIdFilters.push(
        ...currentBaseFilters
          .filter((f) => f.group === 'volumeId')
          .map((f) => ({ ...f, name: `id:${f.name}` }))
      );
      appliedAccountFilters.push(...currentBaseFilters.filter((f) => f.group === 'accountId'));
      appliedVolumeTagFilters.push(
        ...currentBaseFilters.filter((f) => String(f.group).startsWith('tag:'))
      );
    }

    const autoscalingStateRaw = searchParams.get(AUTOSCALING_STATE_PARAM_KEY) || '';
    const autoscalingStateIds = autoscalingStateRaw.split(',').filter(Boolean);
    const autoscalingCount = autoscalingStateIds.reduce((acc, id) => {
      if (id === 'as_state_on' || id === 'as_state_off' || id === 'as_state_unsupported') {
        return acc + 1;
      }
      return acc;
    }, 0);
    const baseFilterCount = currentBaseFilters.filter((f) => f.group !== 'autoscalingState').length;
    const activeFilterCount = baseFilterCount + autoscalingCount;

    return {
      appliedClusterNameFilters,
      appliedInstanceFilters,
      appliedVolumeIdFilters,
      appliedVolumeTagFilters,
      appliedAccountFilters,
      appliedAutoscaleState: autoscalingStateIds,
      activeFilterCount,
      currentBaseFilters,
    };
  }, [apiFilters, currentBaseFilters, searchParams]);
}
